import {
   Flex,
   Button,
   useBreakpoint,
   Popover,
   PopoverContent,
   PopoverArrow,
   PopoverBody,
   HStack,
   Grid,
} from '@chakra-ui/react';
import { faGaugeMin } from '@fortawesome/pro-regular-svg-icons/faGaugeMin';
import { faGaugeLow } from '@fortawesome/pro-regular-svg-icons/faGaugeLow';
import { faGaugeMed } from '@fortawesome/pro-regular-svg-icons/faGaugeMed';
import { faGaugeHigh } from '@fortawesome/pro-regular-svg-icons/faGaugeHigh';
import { faGaugeMax } from '@fortawesome/pro-regular-svg-icons/faGaugeMax';
import { faNotdef } from '@fortawesome/pro-regular-svg-icons/faNotdef';
import { faWrench } from '@fortawesome/pro-regular-svg-icons/faWrench';
import { faHospital } from '@fortawesome/pro-regular-svg-icons/faHospital';
import { faPenLine } from '@fortawesome/pro-regular-svg-icons/faPenLine';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { faCircleStar } from '@fortawesome/pro-regular-svg-icons/faCircleStar';
import { faGraduationCap } from '@fortawesome/pro-regular-svg-icons/faGraduationCap';
import { faOctagonExclamation } from '@fortawesome/pro-regular-svg-icons/faOctagonExclamation';
import { faImages } from '@fortawesome/pro-regular-svg-icons/faImages';
import { faHighlighterLine } from '@fortawesome/pro-regular-svg-icons/faHighlighterLine';
import { faCamera } from '@fortawesome/pro-regular-svg-icons/faCamera';
import { faCode } from '@fortawesome/pro-regular-svg-icons/faCode';
import { faListUl } from '@fortawesome/pro-regular-svg-icons/faListUl';
import { faScissors } from '@fortawesome/pro-regular-svg-icons/faScissors';
import { faFileCircleQuestion } from '@fortawesome/pro-regular-svg-icons/faFileCircleQuestion';
import { faFileCircleXmark } from '@fortawesome/pro-regular-svg-icons/faFileCircleXmark';
import { faImageSlash } from '@fortawesome/pro-regular-svg-icons/faImageSlash';
import { faFaceClouds } from '@fortawesome/pro-regular-svg-icons/faFaceClouds';
import { faHammerCrash } from '@fortawesome/pro-regular-svg-icons/faHammerCrash';
import { faCameraViewfinder } from '@fortawesome/pro-regular-svg-icons/faCameraViewfinder';
import { faFiles } from '@fortawesome/pro-regular-svg-icons/faFiles';
import { faTrashClock } from '@fortawesome/pro-regular-svg-icons/faTrashClock';
import { faPenField } from '@fortawesome/pro-regular-svg-icons/faPenField';
import { faSplit } from '@fortawesome/pro-regular-svg-icons/faSplit';
import { faMountain } from '@fortawesome/pro-regular-svg-icons/faMountain';
import { faBoxArchive } from '@fortawesome/pro-regular-svg-icons/faBoxArchive';
import { faRoute } from '@fortawesome/pro-regular-svg-icons/faRoute';
import { faFileCircleExclamation } from '@fortawesome/pro-regular-svg-icons/faFileCircleExclamation';
import { faMessageCaptions } from '@fortawesome/pro-regular-svg-icons/faMessageCaptions';
import { faHandshake } from '@fortawesome/pro-regular-svg-icons/faHandshake';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faClock } from '@fortawesome/pro-solid-svg-icons/faClock';
import { FlagBadge, FlagBadgeStyle } from './FlagBadge';
import { _js } from '@ifixit/localize';
import { Dispatch, useMemo, useState } from 'react';
import { FlexScrollGradient } from './FlexScrollGradient';
import { useHiddenWrap } from 'breadcrumbs/FlexHiddenWrap';
import { GuideData } from './GuideHeader';
import { useHistoryState } from './HistoryState';
import { HoverPopoverTrigger } from './HoverPopoverTrigger';

type DifficultyTheming = {
   icon: IconDefinition;
} & FlagBadgeStyle;

export enum GuideDifficulty {
   VeryEasy = 1,
   Easy = 2,
   Moderate = 3,
   Difficult = 4,
   VeryDifficult = 5,
}

const DifficultyToolTipLookup: { [_key in GuideDifficulty]: string } = {
   [GuideDifficulty.VeryEasy]: _js('Difficulty rating: Very easy! No disassembly required.'),
   [GuideDifficulty.Easy]: _js(
      'Difficulty rating: Easy! Minimal disassembly, using only basic tools.'
   ),
   [GuideDifficulty.Moderate]: _js(
      'Difficulty rating: Moderate. Follow instructions carefully to succeed.'
   ),
   [GuideDifficulty.Difficult]: _js(
      'Difficulty rating: Difficult. Proceed with caution and patience.'
   ),
   [GuideDifficulty.VeryDifficult]: _js(
      'Difficulty rating: Very. Bring your best skills and tools.'
   ),
};

const DifficultyThemeLookup: { [_key in GuideDifficulty]: DifficultyTheming } = {
   [GuideDifficulty.VeryDifficult]: {
      icon: faGaugeMax,
      themeColor: 'red',
      background: 'red.200',
      borderColor: 'red.300',
      color: 'red.800',
      iconColor: 'red.700',
   },
   [GuideDifficulty.Difficult]: {
      icon: faGaugeHigh,
      themeColor: 'red',
      background: 'red.100',
      borderColor: 'red.300',
      color: 'red.700',
      iconColor: 'red.500',
   },
   [GuideDifficulty.Moderate]: {
      icon: faGaugeMed,
      themeColor: 'amber',
      background: 'amber.100',
   },
   [GuideDifficulty.Easy]: {
      icon: faGaugeLow,
      themeColor: 'green',
      background: 'green.100',
      borderColor: 'green.400',
      color: 'green.700',
      iconColor: 'green.500',
   },
   [GuideDifficulty.VeryEasy]: {
      icon: faGaugeMin,
      themeColor: 'green',
      background: 'green.200',
      borderColor: 'green.400',
      color: 'green.800',
      iconColor: 'green.700',
   },
};

const FAIconMapping: {
   [key: string]: IconDefinition;
} = {
   wrench: faWrench,
   hospital: faHospital,
   'pen-line': faPenLine,
   user: faUser,
   'circle-star': faCircleStar,
   'graduation-cap': faGraduationCap,
   'octagon-exclamation': faOctagonExclamation,
   images: faImages,
   'highlighter-line': faHighlighterLine,
   camera: faCamera,
   code: faCode,
   'list-ul': faListUl,
   scissors: faScissors,
   'file-circle-question': faFileCircleQuestion,
   'file-circle-xmark': faFileCircleXmark,
   'image-slash': faImageSlash,
   'face-clouds': faFaceClouds,
   'hammer-crash': faHammerCrash,
   'camera-viewfinder': faCameraViewfinder,
   files: faFiles,
   'trash-clock': faTrashClock,
   'pen-field': faPenField,
   split: faSplit,
   mountain: faMountain,
   'box-archive': faBoxArchive,
   route: faRoute,
   'file-circle-exclamation': faFileCircleExclamation,
   'message-captions': faMessageCaptions,
   handshake: faHandshake,
};

const FlagBadgeStyleMapping: {
   [key: string]: FlagBadgeStyle;
} = {
   GUIDE_STARRED: { themeColor: 'blue', background: 'blue.100' },
   GUIDE_OEM: { themeColor: 'green' },
   MANUFACTURER_PROVIDED: { themeColor: 'green' },
};

const FlagBadgePriorityMapping: {
   [key: string]: number;
} = {
   GUIDE_STARRED: 3,
   GUIDE_OEM: 2,
   MANUFACTURER_PROVIDED: 1,
};

export const DefaultFlagStyle: FlagBadgeStyle = {
   themeColor: 'amber',
   background: 'white',
};

const MoreButtonReservedBuffer = 100;
const MoreButtonGap = 10;
const ItemGap = 6;

export type FlagSectionProps = Pick<
   GuideData,
   'difficulty' | 'difficultyName' | 'timeRequired' | 'flags' | 'isTeardown'
>;
export function FlagSection({
   difficulty,
   timeRequired,
   flags,
   isTeardown,
   difficultyName,
}: FlagSectionProps) {
   const breakpoint = useBreakpoint({ ssr: false });
   const [isExpanded, setIsExpanded] = useHistoryState(false, 'flagSectionExpanded');
   const isMobile = breakpoint == 'base';
   const nonFullWidthFlags = flags.filter(flag => !flag.fullWidth);

   const hasContent = !isTeardown || flags.length > 0;

   if (!hasContent) {
      return null;
   }

   if (isMobile) {
      return (
         <FlexScrollGradient overflowX="hidden" paddingTop="12px" paddingBottom="16px">
            <HStack spacing={`${ItemGap}px`} className="flags-list">
               <StaticFlagSection
                  difficultyName={difficultyName}
                  isTeardown={isTeardown}
                  difficulty={difficulty}
                  timeRequired={timeRequired}
                  flags={nonFullWidthFlags}
               />
            </HStack>
         </FlexScrollGradient>
      );
   }

   return (
      <DynamicFlagSection
         difficultyName={difficultyName}
         difficulty={difficulty}
         timeRequired={timeRequired}
         flags={nonFullWidthFlags}
         isTeardown={isTeardown}
         isExpanded={isExpanded}
         setIsExpanded={setIsExpanded}
      />
   );
}

function DynamicFlagSection({
   difficulty,
   difficultyName,
   timeRequired,
   flags,
   isExpanded,
   isTeardown,
   setIsExpanded,
}: FlagSectionProps & {
   isExpanded: boolean;
   setIsExpanded: Dispatch<boolean>;
}) {
   const [hiddenFlagCount, setHiddenFlagCount] = useState<number>(0);
   const [left, setLeft] = useState<number>();

   const onDesktopFlagWrap = useMemo(() => {
      return (children: HTMLElement[]) => setHiddenFlagCount(() => children.length);
   }, []);

   const onDesktopFlagShown = (children: HTMLElement[], container: HTMLElement) => {
      const childrenWidthsMinusGap = children
         .map(child => child.offsetWidth)
         .reduce((a, b) => a + b, 0);
      const childrenWidth = ItemGap * (children.length - 1) + childrenWidthsMinusGap;
      const offset = container.offsetWidth - childrenWidth;
      setLeft(-offset + MoreButtonGap);
   };

   const showLink = !isExpanded && hiddenFlagCount > 0;

   const hiddenWrapProps = useHiddenWrap<HTMLDivElement>({
      wrappedChildrenEffect: onDesktopFlagWrap,
      shownChildrenEffect: onDesktopFlagShown,
   });

   const flagSection = (
      <StaticFlagSection
         difficulty={difficulty}
         difficultyName={difficultyName}
         timeRequired={timeRequired}
         flags={flags}
         isTeardown={isTeardown}
      />
   );

   return (
      <Flex paddingTop="12px" paddingBottom="16px" alignItems="center">
         {isExpanded ? (
            <Grid {...hiddenWrapProps} gap={`${ItemGap}px`} maxWidth="100%" className="flags-list">
               {flagSection}
            </Grid>
         ) : (
            <HStack
               {...hiddenWrapProps}
               spacing={`${ItemGap}px`}
               maxWidth={`calc(100% - ${MoreButtonReservedBuffer}px)`}
               height={{ base: '28px', sm: '30px' }}
               overflow="hidden"
               className="flags-list"
            >
               {flagSection}
            </HStack>
         )}
         {showLink && (
            <Button
               variant="link"
               position="relative"
               left={`${left}px`}
               fontSize="14px"
               color="gray.700"
               _visited={{ color: 'gray.700' }}
               _hover={{ textDecoration: 'none', color: 'gray.900' }}
               onClick={() => setIsExpanded(!isExpanded)}
               tabIndex={0}
            >
               {_js('+%1 more', hiddenFlagCount.toString())}
            </Button>
         )}
      </Flex>
   );
}

function StaticFlagSection({
   difficulty,
   difficultyName,
   timeRequired,
   flags,
   isTeardown,
}: FlagSectionProps) {
   const sortedFlags = flags.sort(
      (a, b) =>
         (FlagBadgePriorityMapping[b.flagid] || -1) - (FlagBadgePriorityMapping[a.flagid] || -1)
   );
   const Flags = sortedFlags.map(flag => {
      const badgeStyles = FlagBadgeStyleMapping[flag.flagid] || DefaultFlagStyle;
      const badgeProps = 'image' in flag ? { imageSrc: flag.image } : { icon: getIcon(flag.icon) };
      return (
         <Flex key={flag.title} className="flag">
            <Popover trigger="hover" strategy="fixed">
               <HoverPopoverTrigger>
                  <FlagBadge {...badgeStyles} {...badgeProps} className="flagTitle">
                     {flag.title}
                  </FlagBadge>
               </HoverPopoverTrigger>
               <PopoverContent>
                  <PopoverArrow backgroundColor="gray.800" />
                  <PopoverBody
                     borderRadius="md"
                     backgroundColor="gray.800"
                     color="white"
                     fontSize="14px"
                     className="flagText"
                  >
                     {flag.text}
                  </PopoverBody>
               </PopoverContent>
            </Popover>
         </Flex>
      );
   });

   if (isTeardown) {
      return <Flex>{Flags}</Flex>;
   }

   return (
      <>
         <Flex>
            <Popover trigger="hover" strategy="fixed">
               <HoverPopoverTrigger>
                  <FlagBadge
                     themeColor="gray"
                     background="gray.100"
                     icon={faClock}
                     className="guide-time-required"
                  >
                     {timeRequired}
                  </FlagBadge>
               </HoverPopoverTrigger>
               <PopoverContent>
                  <PopoverArrow backgroundColor="gray.800" />
                  <PopoverBody
                     borderRadius="md"
                     backgroundColor="gray.800"
                     color="white"
                     fontSize="14px"
                  >
                     {_js(
                        'Rough estimate of how long this takes. (Use this to plan your day, not to feel judged.)'
                     )}
                  </PopoverBody>
               </PopoverContent>
            </Popover>
         </Flex>
         <Flex>
            <Popover trigger="hover" strategy="fixed">
               <HoverPopoverTrigger>
                  <FlagBadge
                     {...DifficultyThemeLookup[difficulty]}
                     className={`guide-difficulty guide-difficulty-${difficulty}`}
                  >
                     {difficultyName}
                  </FlagBadge>
               </HoverPopoverTrigger>
               <PopoverContent>
                  <PopoverArrow backgroundColor="gray.800" />
                  <PopoverBody
                     borderRadius="md"
                     backgroundColor="gray.800"
                     color="white"
                     fontSize="14px"
                  >
                     {DifficultyToolTipLookup[difficulty]}
                  </PopoverBody>
               </PopoverContent>
            </Popover>
         </Flex>
         {Flags}
      </>
   );
}

export function getIcon(iconName: string): IconDefinition {
   return FAIconMapping[iconName] || faNotdef;
}
